/**
 * CTO landing page — /app/landing/cto
 *
 * Strategic single-fold view: top risks, frontier-AI exposure, vendor
 * concentration, regulation clocks, ChangeProposal status, ROI. Pure
 * UI composition over existing read endpoints — no new backend logic.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  Sparkles, AlertTriangle, Building2, Brain, AlarmClock, Users,
  Send, GitPullRequest, LayoutGrid, FileText,
} from 'lucide-react'
import { api } from '../lib/api'
import {
  LandingHeader, HeroTile, TwoColumnBody, Panel, EmptyState,
  StickyFooter, ListRow, safeNum,
} from '../components/landing/LandingShell'

interface RiskAsset { golden_id: string; total: number; band: string; archetype?: string }
interface Actor { actor: string; campaign?: string; ttp_count?: number; sector_match?: number }
interface VendorRow { vendor: string; share_pct?: number; assets?: number }
interface RegTimer { id: string; framework?: string; due_at?: string; overdue?: boolean }

export default function CtoLandingPage() {
  const [refreshing, setRefreshing] = useState(false)
  const [risks, setRisks]               = useState<RiskAsset[]>([])
  const [actors, setActors]             = useState<Actor[]>([])
  const [vendors, setVendors]           = useState<VendorRow[]>([])
  const [timers, setTimers]             = useState<RegTimer[]>([])
  const [proposals, setProposals]       = useState<number>(0)
  const [savings, setSavings]           = useState<{ annual: number; fte: number; cost_mtd?: number } | null>(null)
  const [frontierVendors, setFrontier]  = useState<number>(0)
  const [openIncidents, setIncidents]   = useState<number>(0)

  // Best-effort: every API call is independent and may 404 on fresh
  // installs. We never throw — empty data triggers the EmptyState UI.
  const load = useCallback(() => {
    setRefreshing(true)
    const j = (p: Promise<any>) => p.catch(() => ({ data: null }))
    Promise.all([
      j(api.get('/api/cdm/risk', { params: { limit: 5 } })),
      j(api.get('/api/cdm/threat-attribution', { params: { limit: 3 } })),
      j(api.get('/api/cdm/vendor-concentration', { params: { top_n: 5 } })),
      j(api.get('/api/cdm/regulation-timers', { params: { state: 'pending_initial', overdue: true } })),
      j(api.get('/api/cdm/changes/proposals', { params: { state: 'awaiting_approval' } })),
      j(api.get('/api/savings/summary')),
      j(api.get('/api/cdm/frontier-overlay')),
      j(api.get('/api/incidents', { params: { limit: 1 } })),
    ]).then(([r, a, v, t, c, s, f, i]) => {
      setRisks(r.data?.assets || r.data?.results || [])
      setActors(a.data?.actors || a.data?.results || [])
      setVendors(v.data?.vendors || v.data?.results || [])
      setTimers(t.data?.timers || t.data?.results || [])
      setProposals(c.data?.proposals?.length ?? c.data?.count ?? 0)
      setSavings(s.data ? {
        annual: Math.round(s.data.annual_rate_hours || 0),
        fte:    s.data.fte_equivalent || 0,
        cost_mtd: s.data.cost_mtd_usd,
      } : null)
      setFrontier(f.data?.vendor_count ?? f.data?.vendors?.length ?? 0)
      setIncidents(i.data?.open ?? i.data?.count ?? 0)
    }).finally(() => setRefreshing(false))
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(load, 60_000) // auto-refresh time-sensitive tiles
    return () => clearInterval(id)
  }, [load])

  const sendDailyReport = async () => {
    setRefreshing(true)
    try {
      await api.post('/api/cdm/report/send').catch(() => {})
    } finally { setRefreshing(false) }
  }

  const criticalRisks = risks.filter(r => r.band === 'critical').length
  const topVendor = vendors[0]
  const overdueTimers = timers.filter(t => t.overdue).length

  return (
    <div className="space-y-4 animate-fade-in pb-16">
      <LandingHeader
        title="CTO Landing"
        subtitle="strategic snapshot · risks · vendors · regulation · ROI"
        persona="cto"
        onRefresh={load}
        refreshing={refreshing}
      />

      {/* Hero — 6 tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <HeroTile label="Critical risks" value={safeNum(criticalRisks)} tone={criticalRisks > 0 ? 'rose' : 'emerald'}
          sub={`top ${risks.length} assets`} to="/app/cdm" />
        <HeroTile label="Open incidents" value={safeNum(openIncidents)} tone={openIncidents > 0 ? 'amber' : 'emerald'}
          to="/app/incidents" />
        <HeroTile label="Top-1 vendor" value={topVendor ? `${(topVendor.share_pct ?? 0).toFixed(0)}%` : '—'}
          tone={(topVendor?.share_pct ?? 0) > 40 ? 'amber' : 'cyan'}
          sub={topVendor?.vendor || 'no concentration data'} to="/app/cdm" />
        <HeroTile label="Frontier-AI vendors" value={safeNum(frontierVendors)} tone="purple"
          to="/app/cdm/frontier-overlay" />
        <HeroTile label="Regulation overdue" value={safeNum(overdueTimers)}
          tone={overdueTimers > 0 ? 'rose' : 'emerald'} to="/app/cdm/regulation-timers" />
        <HeroTile label="Headcount avoided" value={savings ? `${savings.fte.toFixed(1)} FTE` : '—'}
          tone="emerald" sub={savings ? `${(savings.annual / 1000).toFixed(1)}k h/yr` : ''}
          to="/app/savings" />
      </div>

      <TwoColumnBody
        left={(
          <>
            <Panel title="Top 5 risk assets" viewAllTo="/app/cdm">
              {risks.length === 0 ? (
                <EmptyState message="No risk-scored assets yet."
                  configureTo="/app/cdm/adapters" configureLabel="configure adapters" />
              ) : (
                <div className="space-y-1">
                  {risks.slice(0, 5).map(r => (
                    <ListRow key={r.golden_id}
                      to={`/app/cdm/investigation/${r.golden_id}`}
                      primary={<span className="font-mono text-[10px]">{r.golden_id.slice(0, 12)}…</span>}
                      secondary={r.archetype}
                      right={<span className={r.band === 'critical' ? 'text-rose-light' : 'text-amber-light'}>
                        {r.total} · {r.band}
                      </span>} />
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Top 3 active actor campaigns" viewAllTo="/app/threat-actors">
              {actors.length === 0 ? (
                <EmptyState message="No active threat-attribution data."
                  configureTo="/app/cdm/attribution" configureLabel="open attribution lens" />
              ) : (
                <div className="space-y-1">
                  {actors.slice(0, 3).map(a => (
                    <ListRow key={a.actor}
                      to={`/app/actor/${encodeURIComponent(a.actor)}`}
                      primary={a.actor}
                      secondary={a.campaign || `${a.ttp_count ?? 0} TTPs`}
                      right={a.sector_match !== undefined ? `${a.sector_match} sector match` : ''} />
                  ))}
                </div>
              )}
            </Panel>
          </>
        )}
        right={(
          <>
            <Panel title="Vendor concentration" viewAllTo="/app/cdm">
              {vendors.length === 0 ? (
                <EmptyState message="Vendor concentration not computed."
                  configureTo="/app/cdm/adapters" configureLabel="configure adapters" />
              ) : (
                <div className="space-y-1.5">
                  {vendors.slice(0, 5).map(v => (
                    <div key={v.vendor} className="text-[11px]">
                      <div className="flex justify-between">
                        <span className="text-text-primary truncate">{v.vendor}</span>
                        <span className="font-mono text-text-secondary">{(v.share_pct ?? 0).toFixed(1)}%</span>
                      </div>
                      <div className="h-1.5 bg-surface-raised rounded-full overflow-hidden">
                        <div className="h-full bg-blue/60" style={{ width: `${Math.min(100, v.share_pct ?? 0)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Cost MTD trend" viewAllTo="/app/savings">
              {savings == null || savings.cost_mtd == null ? (
                <EmptyState message="No cost data yet." configureTo="/app/connectors-manage"
                  configureLabel="configure cost connector" />
              ) : (
                <div className="text-center py-3">
                  <div className="text-2xl font-bold font-mono text-emerald">${(savings.cost_mtd / 1000).toFixed(1)}k</div>
                  <div className="text-[10px] text-text-muted mt-1">spend month-to-date</div>
                </div>
              )}
            </Panel>

            <Panel title="ChangeProposals awaiting you" viewAllTo="/app/cdm/changes">
              <div className="text-center py-2 text-[11px]">
                <span className="font-mono text-lg font-bold text-amber-light">{proposals}</span>
                <div className="text-text-muted">awaiting_approval</div>
              </div>
            </Panel>
          </>
        )}
      />

      <StickyFooter actions={[
        { label: 'Send daily report',     icon: Send, tone: 'primary',   onClick: sendDailyReport },
        { label: 'Open ChangeProposal',   icon: GitPullRequest, tone: 'secondary', to: '/app/cdm/changes' },
        { label: 'Executive summary',     icon: LayoutGrid, tone: 'ghost', to: '/app/executive' },
        { label: 'Daily report page',     icon: FileText, tone: 'ghost', to: '/app/cdm/report' },
      ]} />
    </div>
  )
}

// Suppress unused-import warnings on the icon set used only in JSX above.
void Sparkles; void AlertTriangle; void Building2; void Brain; void AlarmClock; void Users
